import { api } from "../api";
import { getGl } from "../engine/glRegistry";
import { useCameraStore } from "../stores/cameraStore";
import { useUiStore } from "../stores/uiStore";

/** Settle time for camera-home animation (~0.83s at 1.2 progress/sec) plus margin. */
const HOME_SETTLE_MS = 900;

/**
 * Max cover width. Covers are only shown on landing SpaceCards (~card-sized,
 * h-36); 512px is enough for 2x DPR and keeps uploads well under the body limit.
 */
const MAX_THUMB_WIDTH = 512;

let inFlight = false;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function waitFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    let remaining = count;
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

function blobFromCanvas(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), "image/png");
    } catch {
      resolve(null);
    }
  });
}

/**
 * Scale the live WebGL canvas down to a gallery-sized PNG.
 * Avoids 413 Payload Too Large from full-resolution retina captures.
 */
function downscaleToThumbnail(source: HTMLCanvasElement): Promise<Blob | null> {
  const srcW = source.width || 1;
  const srcH = source.height || 1;
  const scale = Math.min(1, MAX_THUMB_WIDTH / srcW);
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  if (scale >= 1) {
    return blobFromCanvas(source);
  }

  const offscreen = document.createElement("canvas");
  offscreen.width = width;
  offscreen.height = height;
  const ctx = offscreen.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(source, 0, 0, width, height);
  return blobFromCanvas(offscreen);
}

/**
 * Capture the live sandbox WebGL canvas as a PNG blob.
 *
 * Reads the renderer from glRegistry (registered by SandboxCanvas) because this
 * runs outside React/R3F. Requires `preserveDrawingBuffer: true` on the Canvas.
 * Returns null when the canvas is unmounted or capture fails.
 */
export async function captureSpaceThumbnail(): Promise<Blob | null> {
  // Null when sandbox Canvas is not mounted (or tore down mid-capture).
  const renderer = getGl();
  if (!renderer) return null;

  // Let R3F finish presenting the current frame before reading pixels.
  await waitFrames(2);

  return downscaleToThumbnail(renderer.domElement);
}

/**
 * Capture the sandbox view after camera-home settle and upload as the space cover.
 * Non-blocking for callers — failures are logged and ignored.
 */
export async function autoUpdateThumbnail(
  spaceId: string | null,
): Promise<void> {
  if (!spaceId || inFlight) return;

  inFlight = true;
  try {
    useUiStore.getState().clearSelection();
    useCameraStore.getState().requestHome();
    await delay(HOME_SETTLE_MS);

    const blob = await captureSpaceThumbnail();
    if (!blob) return;

    await api.uploadScreenshot(spaceId, blob);
  } catch (err) {
    console.warn("Auto thumbnail update failed:", err);
  } finally {
    inFlight = false;
  }
}

/** Test-only: reset in-flight guard between cases. */
export function __resetAutoThumbnailForTests(): void {
  inFlight = false;
}
